import { Phone, MessageSquare } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { analytics } from '@/lib/analytics';
import { contactInfo } from '@/lib/contactInfo';

interface ContactOptionsProps {
  variant?: 'cards' | 'inline' | 'compact';
  textMessageBody?: string;
  showHours?: boolean;
  className?: string;
  onCallClick?: () => void;
  onTextClick?: () => void;
}

export const ContactOptions = ({
  variant = 'cards',
  textMessageBody = "Hi! I'm interested in BlueLux pool service.",
  showHours = true,
  className = '',
  onCallClick,
  onTextClick,
}: ContactOptionsProps) => {
  const phoneNumber = contactInfo.phone.actual;
  const phoneDisplay = contactInfo.phone.display;

  const handleCallClick = () => {
    analytics.trackPhoneCall();
    onCallClick?.();
  };

  const handleTextClick = () => {
    analytics.trackPhoneCall(); // Track as phone interaction
    onTextClick?.();
  };

  const encodedMessage = encodeURIComponent(textMessageBody);
  const smsLink = `sms:${phoneNumber}?&body=${encodedMessage}`;

  if (variant === 'cards') {
    return (
      <div className={`grid md:grid-cols-2 gap-6 ${className}`}>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="p-6 text-center">
            <Phone className="w-12 h-12 mx-auto mb-4 text-gold" />
            <h4 className="text-xl font-bold mb-2">Call Us</h4>
            <a 
              href={`tel:${phoneNumber}`}
              className="text-3xl font-bold text-gold hover:text-gold/80 transition-colors block mb-2"
              onClick={handleCallClick}
            >
              {phoneDisplay}
            </a>
            <p className="text-sm text-blue-100">({phoneNumber})</p>
            {showHours && (
              <p className="text-sm text-blue-200 mt-3">Mon-Fri 8AM-6PM | Sat 9AM-4PM</p>
            )}
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="p-6 text-center">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gold" />
            <h4 className="text-xl font-bold mb-2">Text Us</h4>
            <a 
              href={smsLink}
              className="text-3xl font-bold text-gold hover:text-gold/80 transition-colors block mb-2"
              onClick={handleTextClick}
            >
              {phoneDisplay}
            </a>
            <p className="text-sm text-blue-100">({phoneNumber})</p>
            {showHours && (
              <p className="text-sm text-blue-200 mt-3">Quick response during business hours</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`flex flex-col sm:flex-row gap-4 justify-center ${className}`}>
        <Button 
          size="lg" 
          variant="outline"
          asChild
        >
          <a href={`tel:${phoneNumber}`} onClick={handleCallClick}>
            <Phone className="w-5 h-5 mr-2" />
            Call {phoneDisplay}
          </a>
        </Button>
        <Button 
          size="lg" 
          variant="outline"
          asChild
        >
          <a href={smsLink} onClick={handleTextClick}>
            <MessageSquare className="w-5 h-5 mr-2" />
            Text Us
          </a>
        </Button>
      </div>
    );
  }

  // compact variant
  return (
    <div className={`flex flex-wrap gap-3 items-center ${className}`}>
      <a 
        href={`tel:${phoneNumber}`}
        className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
        onClick={handleCallClick}
      >
        <Phone className="w-4 h-4" />
        <span>{phoneDisplay}</span>
      </a>
      <span className="text-muted-foreground">|</span>
      <a 
        href={smsLink}
        className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
        onClick={handleTextClick}
      >
        <MessageSquare className="w-4 h-4" />
        <span>Text Us</span>
      </a>
    </div>
  );
};
